import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import InfoDisplay from "@/components/InfoDisplay";
import Layout from "../../shared/components/Layout";
import { formatDate, formatMonth } from '@/utils/format';
import { createParams } from "../types";
import createRegistration from "../api/createRegistration";

const RegistrationConfirm: React.FC = () => {
  const navigate = useNavigate();

  const displayPlan = (plan: string, price: string): string => {
    if(plan === 'both') return `両眼プラン(${price}円/月)`
    if(plan === 'right') return `片眼プラン(右眼)(${price}円/月)`
    if(plan === 'left') return `片眼プラン(左眼)(${price}円/月)`
    return '';
  }

  const handleButtonClick = (orderId: string | undefined, event: React.MouseEvent<HTMLButtonElement>): void => {
    if(!orderId) {
      const confirmRegistration = window.confirm('新規契約対象のレンズ注文が存在しませんが、登録しますか？');
      if (!confirmRegistration) {
        event.preventDefault();
      }
    }
  };

  const renderPreviousPage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate("/facility/flat_rates/registration", { state: { fromConfirmPage: true } });
    window.scrollTo(0, 0);
  }

  const [facilityCode, setFacilityCode] = useState<string>("");
  const [flatRateCode, setFlatRateCode] = useState<string>("");
  const [customer, setCustomer] = useState({
    custCodeSuffix: "",
  })
  const [applicant, setApplicant] = useState({
    nameKana: "",
    birthday: "",
  })
  const [isSameApplicant, setIsSameApplicant] = useState<boolean>(true);
  const [consumer, setConsumer] = useState({
    nameKana: "",
  })
  const [flatRateAgreement, setFlatRateAgreement] = useState({
    lensPriceId: "",
    price: "",
    plan: "",
    startDate: "",
    startMonth: "",
  })
  const [orderId, setOrderId] = useState<string>();

  const [usageFeeOccurMonth, setUsageFeeOccurMonth] = useState<string>("");
  const [usageFeeFirstPaymentMonth, setUsageFeeFirstPaymentMonth] = useState<string>("");

  useEffect(() => {
    const storedCustomer = localStorage.getItem("customer");
    if(storedCustomer) {
      setCustomer(JSON.parse(storedCustomer));
    }

    const storedApplicant = localStorage.getItem("applicant");
    if(storedApplicant) {
      setApplicant(JSON.parse(storedApplicant));
    }

    const storedConsumer = localStorage.getItem("consumer");
    if(storedConsumer) {
      setConsumer(JSON.parse(storedConsumer));
    }

    const storedFlatRateAgreement = localStorage.getItem("flatRateAgreement");
    if(storedFlatRateAgreement) {
      setFlatRateAgreement(JSON.parse(storedFlatRateAgreement));
    }

    const storedIsSameApplicant = localStorage.getItem("isSameApplicant");
    if(storedIsSameApplicant) {
      setIsSameApplicant(JSON.parse(storedIsSameApplicant));
    }

    const storedFacilityCode = localStorage.getItem("facilityCode");
    if(storedFacilityCode) {
      setFacilityCode(JSON.parse(storedFacilityCode));
    }

    const storedFlatRateCode = localStorage.getItem("flatRateCode");
    if(storedFlatRateCode) {
      setFlatRateCode(JSON.parse(storedFlatRateCode));
    }

    const storedUsageFeeOccurMonth = localStorage.getItem("usageFeeOccurMonth");
    if(storedUsageFeeOccurMonth) {
      setUsageFeeOccurMonth(JSON.parse(storedUsageFeeOccurMonth));
    }

    const storedUsageFeeFirstPaymentMonth = localStorage.getItem("usageFeeFirstPaymentMonth");
    if(storedUsageFeeFirstPaymentMonth) {
      setUsageFeeFirstPaymentMonth(JSON.parse(storedUsageFeeFirstPaymentMonth));
    }

    const storedOrderId = localStorage.getItem("orderId");
    if(storedOrderId) {
      setOrderId(storedOrderId);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const params: createParams = {
      facility: {facilityCode: facilityCode},
      customer: customer,
      applicant: applicant,
      consumer: consumer,
      flatRateAgreement: {
        ...flatRateAgreement,
        orderId: orderId,
      }
    }

    try {
      const res = await createRegistration(params);
      if(res.status === 201) {
        localStorage.removeItem("customer");
        localStorage.removeItem("applicant");
        localStorage.removeItem("consumer");
        localStorage.removeItem("flatRateAgreement");
        localStorage.removeItem("isSameApplicant");
        localStorage.removeItem("facilityCode");
        localStorage.removeItem("flatRateCode");
        localStorage.removeItem("usageFeeOccurMonth");
        localStorage.removeItem("usageFeeFirstPaymentMonth");
        localStorage.removeItem("orderId");

        navigate("/facility/flat_rates/registration/complete");
      } else if(res.status !== 201) {
        navigate("/facility/flat_rates/registration");
        window.scrollTo(0, 0);
        console.error(`status: ${res.status}, message: ${res.statusText}`);
      }
    } catch (err: any) {
      console.log('An unexpected error occurred:', err.message);
    }
  }

  return (
    <React.Fragment>
      <Layout>
        <main id="page-facility_login" className="wrapper">
          <section className="sec-contact">
            <div className="inner">
              <h1 className="c-ttl__01">新規契約申込入力内容確認</h1>
              <div className="contact-box">
                <div className="contact-box__left">
                  <div className="mbpc-98 mbsp-32">
                    <form onSubmit={handleSubmit}>
                      <div className="contact-box__form js-item" id="id-01">
                        <h2 className="c-ttl__02">お客様情報</h2>
                        <InfoDisplay label="契約種別" value={"定額制契約"} />
                        <InfoDisplay label="お客様ID" value={`${flatRateCode} ${customer.custCodeSuffix}`} />
                        <InfoDisplay label="申込者 (カナ)" value={applicant.nameKana} />
                        <InfoDisplay label={<>申込者生年月日<br/>(仮パスワード)</>} value={formatDate(applicant.birthday)} />
                        <InfoDisplay label="使用者（カナ）" value={isSameApplicant ? "申込者と同一" : consumer.nameKana} />
                      </div>
                      <div className="contact-box__form mbsp-29 js-item" id="id-02">
                        <h2 className="c-ttl__02">契約詳細</h2>
                        <InfoDisplay label="ご契約プラン" value={displayPlan(flatRateAgreement.plan, flatRateAgreement.price)} />
                        <InfoDisplay label="サービス利用開始日" value={formatDate(flatRateAgreement.startDate)} />
                        <InfoDisplay label="サービス利用開始月" value={formatMonth(flatRateAgreement.startMonth)} />
                        <InfoDisplay label="利用料請求発生月" value={usageFeeOccurMonth} />
                        <InfoDisplay label="利用料初回決済月（月末）" value={usageFeeFirstPaymentMonth} />
                        <InfoDisplay label="レンズ注文" value={orderId ? "あり" : "なし"} />
                      </div>
                      <button type="submit" className="c-btn__01" onClick={(event) => handleButtonClick(orderId, event)}>この内容で登録する</button>
                    </form>
                    <div className="c-al--c">
                      <button className="c-btn__02" onClick={renderPreviousPage}>前のページに戻る</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </Layout>
    </React.Fragment>
  )
}

export default RegistrationConfirm;